import { AuthDialog } from "app-constants/constants"
import useAuthRedirect from "../useAuthRedirect"
import { api } from "lib/api"
import useCurrent from "lib/use-current"
import ConventionUserExhibitorFavorite from "models/convention-api/v1/ConventionUserExhibitorFavorite"
import { useEffect, useMemo, useState } from "react"

export interface UseFavoriteExhibitorsReturn {
  isLoading: boolean
  isReady: boolean
  list: ConventionUserExhibitorFavorite[]
  listMap: Record<string, string>
  isFavorite: (exhibitorId: string) => boolean
  addFavorite: (exhibitorId: string) => void
  removeFavorite: (exhibitorId: string) => void
  toggleFavorite: (exhibitorId: string) => void
}

interface UseFavoriteExhibitorsConfig {
  includes?: string[]
}

export const useFavoriteExhibitors = (
  config: UseFavoriteExhibitorsConfig = {},
): UseFavoriteExhibitorsReturn => {
  const current = useCurrent()
  const authRedirect = useAuthRedirect({
    defaultDialog: { dialog: AuthDialog.JOIN, payload: { preamble: '', dialogHeader: 'Sign In' }},
  })
  const [listMap, setListMap] = useState<UseFavoriteExhibitorsReturn["listMap"]>({})

  const favoriteExhibitors = ConventionUserExhibitorFavorite.useAll(() => {
    if (!current) return null
    if (!current?.user) return null

    return {
      includes: config.includes,
    }
  })

  const list = useMemo(() => {
    if (!favoriteExhibitors.data) return []
    return favoriteExhibitors.data.flat()
  }, [favoriteExhibitors.data])

  useEffect(() => {
    if (list.length === 0) return setListMap({})
    setListMap(
      Object.fromEntries(
        list.map(({ convention_provider_exhibitor_id, id }) => [
          convention_provider_exhibitor_id,
          id,
        ]),
      ),
    )
  }, [list])

  const isFavorite = (exhibitorId: string) => !!listMap[exhibitorId]

  const addFavorite = async (exhibitorId: string) => {
    try {
      const response = await api.addUserFavoriteExhibitor(
        { req: undefined },
        current!.user.id,
        exhibitorId,
      )
      setListMap((previousListMap) => ({ ...previousListMap, [exhibitorId]: response?.data?.id }))
    } catch (error) {
      console.error(error)
    }
  }

  const removeFavorite = async (exhibitorId: string) => {
    try {
      const favoriteId = listMap[exhibitorId]
      await api.removeUserFavoriteExhibitor({ req: undefined }, favoriteId)
      setListMap((previousListMap) => {
        const newListMap = { ...previousListMap }
        delete newListMap[exhibitorId]
        return newListMap
      })
    } catch (error) {
      console.error(error)
    }
  }

  const toggleFavorite = (exhibitorId: string) =>
    authRedirect(() => {
      if (isFavorite(exhibitorId)) removeFavorite(exhibitorId)
      else addFavorite(exhibitorId)
    })

  return {
    isLoading: !current || (!favoriteExhibitors.data && favoriteExhibitors.isValidating),
    isReady: Boolean(current && !current.user) || !!favoriteExhibitors.data,
    list,
    listMap,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
  }
}
